/* eslint-disable */
import React, { useEffect, useRef, useState } from "react"
import WeekHoursReportBody from "./tables/WeekHoursReportDataTable/WeekHoursReportBody.controller"
import { generateWeekHoursReport } from "../../../services/report.service"
import ProgressSpinner from "../../../components/atoms/misc/ProgressSpinner.atom"
import useReportFiltersContext from "../../../hooks/contextConsumers/useReportFiltersContext.hook"
import { DateTime } from "luxon"
import ResetButton from "./components/ResetButton"
import UnifiedDatePicker from "../../../components/compounds/UnifiedDatePicker.compound"
import { UNIFIEDDATE_VALUES } from "../../../consts/CompoundRangeDatePicker"
import { BREAKPOINTS } from "../../../consts/responsiveBreakpoints.consts"
import useScreenDimensionsContext from "../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import {
  getAllTypeDayOffHolidaySchemaRules,
  getHolidaysOnDateRange,
} from "../../../services/vacation.service"
import { OverlayPanel } from "../../../components/atoms/OverlayPanel.atom"
import TeamMemberSortFilter from "../../../components/compounds/columnFilters/TeamMemberSortFilter.compound"
import { roundReportData } from "../../../utils/report.utils"
import CustomDialog from "../../../components/molecules/CustomDialog.molecule"
import { xor } from "lodash"
import Button from "../../../components/atoms/Button.atom"

const stringArrAreEqual = (arr1, arr2) => _.xor(arr1, arr2).length === 0

/** @param {import('luxon').DateTime} date */
const getFullWeekFromDate = (date) => {
  const weekStart = date.startOf("week")
  const weekEnd = weekStart.plus({ days: 6 }).endOf("day")
  return {
    weekStart: weekStart.toJSDate(),
    weekEnd: weekEnd.toJSDate(),
  }
}

const sortMembers = (members, currentSortOrder) => {
  if (!members) return []
  let teamMemberReport = members
  if (currentSortOrder !== 1) {
    const sortedData = teamMemberReport.sort((a, b) => {
      if (a.teamMemberIsActive !== b.teamMemberIsActive) {
        return b.teamMemberIsActive - a.teamMemberIsActive
      }
      if (a.teamMemberTagName > b.teamMemberTagName) return -1
      if (a.teamMemberTagName < b.teamMemberTagName) return 1
      return 0
    })
    teamMemberReport = sortedData
  } else {
    const sortedData = teamMemberReport.sort((a, b) => {
      if (a.teamMemberIsActive !== b.teamMemberIsActive) {
        return b.teamMemberIsActive - a.teamMemberIsActive
      }
      if (a.teamMemberTagName < b.teamMemberTagName) return -1
      if (a.teamMemberTagName > b.teamMemberTagName) return 1
      return 0
    })
    teamMemberReport = sortedData
  }

  return teamMemberReport
}

const WeekHoursReport = React.forwardRef((props, tableRef) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reportData, setReportData] = useState({})
  const [filteredReportData, setFilteredReportData] = useState(null)
  const [holidays, setHolidays] = useState([])
  const [schemaRules, setSchemaRules] = useState([])
  const { weekHoursDateRange, setWeekHoursDateRange } =
    useReportFiltersContext()
  const { screenWidth } = useScreenDimensionsContext()
  const isOnMobile = screenWidth <= BREAKPOINTS.MOBILE_BREAKPOINT

  const [recalculateFooter, setRecalculateFooter] = useState(false)

  const [memberSortOrder, setMemberSortOrder] = useState(1)
  const [filteredUsersBuffer, setFilteredUsersBuffer] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])

  useEffect(() => {
    gettingWeekHoursReport()
  }, [weekHoursDateRange])

  useEffect(() => {
    const teamMemberReports =
      filteredReportData ?
        filteredReportData.teamMemberReport
      : reportData?.teamMemberReport
    if (teamMemberReports) {
      setFilteredReportData({
        ...reportData,
        teamMemberReport: sortMembers(teamMemberReports, memberSortOrder),
      })
    }
  }, [memberSortOrder])

  const gettingWeekHoursReport = async () => {
    setError(null)
    setIsLoading(true)
    const fullWeek = getFullWeekFromDate(weekHoursDateRange[0])
    const response = await generateWeekHoursReport(
      DateTime.fromJSDate(fullWeek.weekStart).startOf("day").toISODate(),
      DateTime.fromJSDate(fullWeek.weekEnd).endOf("day").toISODate()
    )
    if (response.success) {
      const reportData = roundReportData(response.result)
      setReportData(reportData)
      gettingHolidays()
      gettingSchemaRules()
    } else {
      setError(response.message)
    }
  }

  const gettingHolidays = async () => {
    // setError(null)
    // setIsLoading(true)
    const fullWeek = getFullWeekFromDate(weekHoursDateRange[0])
    const response = await getHolidaysOnDateRange(
      DateTime.fromJSDate(fullWeek.weekStart).startOf("day").toISODate(),
      DateTime.fromJSDate(fullWeek.weekEnd).endOf("day").toISODate()
    )
    if (response.success) {
      setHolidays(Object.values(response.result).flat())
    } else {
      setError(response.message)
    }
    setIsLoading(false)
  }

  const gettingSchemaRules = async () => {
    const response = await getAllTypeDayOffHolidaySchemaRules()
    if (response.success) {
      setSchemaRules(response.result)
    } else {
      setError(response.message)
    }
    setIsLoading(false)
  }

  const onTeamMemberFiltersClear = () => {
    setFilteredUsersBuffer([])
    setFilteredUsers([])
    setFilteredReportData(null)
    setRecalculateFooter(true)
  }

  const onTeamMemberFiltersApply = () => {
    setFilteredUsers(filteredUsersBuffer)
    setRecalculateFooter(true)
    setFilteredReportData({
      ...reportData,
      teamMemberReport: reportData.teamMemberReport.filter((tm) =>
        filteredUsersBuffer.includes(tm.teamMemberTagName)
      ),
    })
  }

  useEffect(() => {
    setFilteredUsersBuffer([])
    setFilteredUsers([])
    setFilteredReportData(null)
  }, [reportData])

  const tableMemberFilters = (() => {
    const op = useRef(null)

    const teamMembersFilterOptions = (reportData?.teamMemberReport || [])
      .map((tm) => {
        return {
          label: `${tm.teamMemberTagName} - ${tm.teamMemberName} ${tm.teamMemberLastName}`,
          value: tm.teamMemberTagName,
          isActive: tm.teamMemberIsActive,
        }
      })
      .reduce(
        (acc, next) => {
          if (next.isActive) {
            acc[0].items.push(next)
          } else {
            acc[1].items.push(next)
          }
          return acc
        },
        [
          {
            active: true,
            items: [],
          },
          {
            active: false,
            items: [],
          },
        ]
      )
      .filter((el) => el.items.length > 0)

    return (
      <div className="flex align-items-center gap-1 weekly-hours-report-member-filters mr-1">
        <button
          onClick={() => setMemberSortOrder(memberSortOrder === 1 ? 2 : 1)}
          className="ml-1 p-column-filter-menu-button p-link flex align-items-center justify-content-center"
        >
          <span
            className={`p-sortable-column-icon pi pi-fw ${
              memberSortOrder === 1 ?
                "pi-sort-amount-up-alt"
              : "pi-sort-amount-down"
            } mr-0`}
          />
        </button>
        <button
          onClick={(e) => {
            if (isOnMobile) {
              setIsMobileFilterOpen(true)
            } else {
              op.current?.toggle(e)
            }
          }}
          type="button"
          className="p-column-filter-menu-button p-link"
        >
          <span className="pi pi-filter-icon pi-filter"></span>
          <span
            role="presentation"
            className="p-ink"
            style={{
              height: "28px",
              width: "28px",
              top: "14px",
              left: "3.5px",
            }}
          ></span>
        </button>
        <OverlayPanel
          ref={op}
          onHide={() => setFilteredUsersBuffer(filteredUsers)}
          id="overlay-panel-member-filters"
          className="p-column-filter-overlay p-component p-fluid p-column-filter-overlay-menu p-connected-overlay-enter-done"
        >
          <TeamMemberSortFilter
            value={filteredUsersBuffer}
            onChange={setFilteredUsersBuffer}
            teamMembers={teamMembersFilterOptions}
          />
          <div className="p-column-filter-buttonbar">
            <button
              aria-label="Limpiar"
              type="button"
              className="p-button p-component p-button-outlined p-button-sm"
              onClick={() => {
                op.current?.hide()
                onTeamMemberFiltersClear()
              }}
            >
              <span className="p-button-label p-c">Limpiar</span>
              <span role="presentation" className="p-ink"></span>
            </button>
            <button
              type="button"
              className="p-button p-component p-button-sm"
              disabled={stringArrAreEqual(filteredUsersBuffer, filteredUsers)}
              onClick={() => {
                op.current?.hide()
                onTeamMemberFiltersApply()
              }}
            >
              <span className="p-button-label p-c">Aplicar</span>
              <span role="presentation" className="p-ink"></span>
            </button>
          </div>
        </OverlayPanel>
        {isOnMobile && (
          <CustomDialog
            dismissableMask
            visible={isMobileFilterOpen}
            className="mobile-general-report-filters-dialog"
            onHide={() => {
              setIsMobileFilterOpen(false)
              setFilteredUsersBuffer(filteredUsers)
            }}
            withoutDivider
            withoutHeader
            footer={
              <div>
                <button
                  onClick={() => {
                    setIsMobileFilterOpen(false)
                    onTeamMemberFiltersClear()
                  }}
                  className="p-button p-component p-button-primary p-button-outlined border-round-3xl cancel-button"
                >
                  <span className="p-button-label p-c">Limpiar</span>
                </button>
                <button
                  className="p-button p-component p-button-primary border-round-3xl bg-primary-highlight"
                  onClick={() => {
                    setIsMobileFilterOpen(false)
                    onTeamMemberFiltersApply()
                  }}
                  disabled={stringArrAreEqual(
                    filteredUsersBuffer,
                    filteredUsers
                  )}
                >
                  <span className="p-button-label p-c">Aceptar</span>
                </button>
              </div>
            }
          >
            <TeamMemberSortFilter
              value={filteredUsersBuffer}
              onChange={setFilteredUsersBuffer}
              teamMembers={teamMembersFilterOptions}
              customSuffixHeaderIcon={
                <Button
                  onClick={() => setIsMobileFilterOpen(false)}
                  icon="pi pi-times"
                  className="p-multiselect-close p-button-link"
                  aria-label="Close"
                />
              }
              autoFocusSearchBar={false}
            />
          </CustomDialog>
        )}
      </div>
    )
  })()

  return (
    <>
      <div
        style={{
          marginBottom: 17.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <UnifiedDatePicker
          dateRange={weekHoursDateRange}
          setDateRange={setWeekHoursDateRange}
          period={UNIFIEDDATE_VALUES.SINGLEWEEK}
          hidePeriodSelector
          format="'Semana' wo '-' YYYY"
          disabledChevrons={isLoading}
          hideChevron={screenWidth <= BREAKPOINTS.MOBILE_BREAKPOINT}
        />
        <div className="flex align-items-center gap-1">
          {screenWidth < BREAKPOINTS.MOBILE_BREAKPOINT && tableMemberFilters}
          <ResetButton
            onClick={() => {
              setRecalculateFooter(true)
              setFilteredUsers([])
              setFilteredReportData(null)
              setMemberSortOrder(1)
              tableRef.current?.reset()
              setWeekHoursDateRange([
                DateTime.local().startOf("week"),
                DateTime.local().startOf("week"),
              ])
            }}
          />
        </div>
      </div>
      {isLoading ?
        <div className="w-full h-20rem flex align-items-center justify-content-center">
          <ProgressSpinner />
        </div>
      : error ?
        <div>Error: {error}</div>
      : <WeekHoursReportBody
          ref={tableRef}
          reportData={filteredReportData || reportData}
          holidays={holidays}
          schemaRules={schemaRules}
          teamMemberFilters={tableMemberFilters}
          recalculateFooter={recalculateFooter}
          setRecalculateFooter={setRecalculateFooter}
          filteredUsers={filteredUsers}
        />
      }
    </>
  )
})

export default WeekHoursReport
